import React, { useState, useEffect } from 'react';
import axios from '../../axios/axiosconf';
import Swal from 'sweetalert2';

function ModalRipsExito({ show, onClose, ripsData, onDescargar }) {
    const [enviando, setEnviando] = useState(false);
    const [generandoPaquete, setGenerandoPaquete] = useState(false);
    const [facturaValida, setFacturaValida] = useState(null);
    const [validandoFactura, setValidandoFactura] = useState(false);
    const [estadoEnvio, setEstadoEnvio] = useState(null); // null, 'enviado', 'error'
    const [cuv, setCuv] = useState(null);

    useEffect(() => {
        if (show && ripsData && ripsData.factura_id) {
            validarEstadoFactura();
        } else if (show && ripsData && !ripsData.factura_id) {
            // Si no tiene factura, está habilitado
            setFacturaValida({ valida: true, motivo: null });
        }
    }, [show, ripsData]);

    if (!show) return null;

    const { rips_id, factura_id, tipo, estado } = ripsData;

    const validarEstadoFactura = async () => {
        setValidandoFactura(true);
        try {
            const response = await axios.post('/api/rips-sispro/validar-factura', {
                factura_id: factura_id
            });
            setFacturaValida(response.data);
        } catch (error) {
            console.error('Error validando factura:', error);
            setFacturaValida({ 
                valida: false, 
                motivo: 'Error al validar estado de factura' 
            });
        } finally {
            setValidandoFactura(false);
        }
    };

    const handleEnviarSispro = async () => {
        // Confirmar envío
        const result = await Swal.fire({
            title: '¿Enviar RIPS a SISPRO?',
            text: 'Se enviará el RIPS al sistema SISPRO del Ministerio de Salud',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#323a45',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sí, enviar',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        setEnviando(true);
        try {
            const response = await axios.post('/api/rips-sispro/enviar', {
                rips_id: rips_id
            });

            setEstadoEnvio('enviado');
            setCuv(response.data.cuv);

            Swal.fire({
                icon: 'success',
                title: '¡RIPS Enviado!',
                html: `
                    <p>El RIPS fue enviado exitosamente a SISPRO</p>
                    <hr>
                    <p><strong>CUV:</strong> ${response.data.cuv}</p>
                    <p><strong>Fecha de Radicación:</strong> ${response.data.fecha_radicacion}</p>
                `,
                confirmButtonText: 'Aceptar'
            });
        } catch (error) {
            console.error('Error enviando RIPS:', error);
            setEstadoEnvio('error');

            let errorMessage = 'Error al enviar RIPS a SISPRO';
            if (error.response && error.response.data && error.response.data.message) {
                errorMessage = error.response.data.message;
            }

            Swal.fire({
                icon: 'error',
                title: 'Error al Enviar',
                text: errorMessage,
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setEnviando(false);
        }
    };

    const handleGenerarPaquete = async () => {
        setGenerandoPaquete(true);
        try {
            const response = await axios.post('/api/rips-sispro/generar-paquete', {
                rips_id: rips_id
            }, {
                responseType: 'blob'
            });

            // Crear enlace de descarga
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            // Obtener nombre del archivo del header Content-Disposition
            const contentDisposition = response.headers['content-disposition'];
            let fileName = `rips_${rips_id}_paquete.zip`;
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (fileNameMatch && fileNameMatch.length === 2) {
                    fileName = fileNameMatch[1];
                }
            }
            
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            Swal.fire({
                icon: 'success',
                title: 'Paquete Generado',
                text: 'El paquete SISPRO (JSON + XML) ha sido descargado exitosamente',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error generando paquete:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al generar el paquete SISPRO',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setGenerandoPaquete(false);
        }
    };

    const getTipoTexto = () => {
        if (tipo === 'fev') return 'RIPS con Factura Electrónica';
        if (tipo === 'convenio') return 'RIPS por Convenio';
        if (tipo === 'sin_fev') return 'RIPS sin Factura';
        return 'RIPS';
    };

    const getEstadoTexto = () => {
        switch (estado) {
            case 0: return 'Anulado';
            case 1: return 'Generado sin factura';
            case 2: return 'Por convenio';
            case 3: return 'Enviado';
            case 4: return 'Enviado por convenio';
            default: return 'Desconocido';
        }
    };

    const getEstadoBadgeClass = () => {
        switch (estado) {
            case 0: return 'badge-outline-secondary';
            case 1: return 'badge-outline-info';
            case 2: return 'badge-outline-warning';
            case 3: return 'badge-outline-info';
            case 4: return 'badge-outline-info';
            default: return 'badge-outline-secondary';
        }
    };

    const getEstadoEnvioBadge = () => {
        if (estadoEnvio === 'enviado') {
            return <span className="badge badge-outline-secondary"><i className="fas fa-check-circle mr-1"></i>Enviado a SISPRO</span>;
        }
        if (estadoEnvio === 'error') {
            return <span className="badge badge-outline-danger"><i className="fas fa-exclamation-circle mr-1"></i>Error al enviar</span>;
        }
        if (factura_id && facturaValida && !facturaValida.valida) {
            return <span className="badge badge-outline-warning"><i className="fas fa-exclamation-triangle mr-1"></i>Factura pendiente</span>;
        }
        return <span className="badge badge-outline-secondary"><i className="fas fa-clock mr-1"></i>Pendiente de envío</span>;
    };

    const botonEnviarHabilitado = () => {
        if (enviando || estadoEnvio === 'enviado') return false;
        if (factura_id) {
            return facturaValida && facturaValida.valida;
        }
        return true; // Sin factura, siempre habilitado
    };

    const getTooltipEnviar = () => {
        if (estadoEnvio === 'enviado') return 'Este RIPS ya fue enviado a SISPRO';
        if (enviando) return 'Enviando...';
        if (factura_id && facturaValida && !facturaValida.valida) {
            return facturaValida.motivo || 'Debe enviar primero la factura electrónica a la DIAN';
        }
        return 'Enviar RIPS a SISPRO';
    };

    return (
        <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content modal-rips-exito">
                    <div className="modal-header modal-rips-exito__header">
                        <h4 className="modal-title mb-0 d-flex align-items-center">
                            <i className="fas fa-check-circle mr-2 modal-rips-exito__header-icon"></i>
                            RIPS guardado correctamente
                        </h4>
                        <button type="button" className="close" onClick={onClose} aria-label="Cerrar">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="modal-rips-section-title mb-3">
                            <i className="fas fa-info-circle mr-2"></i>
                            Información del RIPS
                        </div>
                        
                        <div className="row">
                            <div className="col-md-4">
                                <p><strong>ID RIPS:</strong></p>
                                <p className="text-muted">{rips_id}</p>
                            </div>
                            <div className="col-md-4">
                                <p><strong>Tipo:</strong></p>
                                <p className="text-muted">{getTipoTexto()}</p>
                            </div>
                            <div className="col-md-4">
                                <p><strong>Estado:</strong></p>
                                <p><span className={`badge ${getEstadoBadgeClass()}`}>{getEstadoTexto()}</span></p>
                            </div>
                        </div>

                        {factura_id && (
                            <div className="row">
                                <div className="col-md-12">
                                    <p><strong>Factura ID:</strong> {factura_id}</p>
                                </div>
                            </div>
                        )}

                        <div className="row mt-2">
                            <div className="col-md-12">
                                <p><strong>Estado de Envío SISPRO:</strong></p>
                                <p>{getEstadoEnvioBadge()}</p>
                            </div>
                        </div>

                        {cuv && (
                            <div className="alert modal-rips-alert-soft mt-2 mb-0">
                                <p className="mb-0"><strong>CUV:</strong> {cuv}</p>
                            </div>
                        )}

                        {factura_id && facturaValida && !facturaValida.valida && (
                            <div className="alert modal-rips-alert-caution mt-2 mb-0">
                                <i className="fas fa-exclamation-triangle mr-2"></i>
                                {facturaValida.motivo}
                            </div>
                        )}

                        <hr />

                        <div className="row">
                            <div className="col-md-6 mb-2">
                                <button 
                                    type="button"
                                    className="btn btn-rips-modal-solid btn-block"
                                    onClick={() => onDescargar(rips_id)}
                                >
                                    <i className="fas fa-download mr-1"></i>
                                    Descargar JSON
                                </button>
                            </div>
                            <div className="col-md-6 mb-2">
                                <button 
                                    type="button"
                                    className="btn btn-secondary btn-block"
                                    onClick={handleGenerarPaquete}
                                    disabled={generandoPaquete}
                                    title="Descargar paquete ZIP con JSON y XML para revisión manual"
                                >
                                    {generandoPaquete ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Generando...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-file-archive"></i> Descargar Paquete SISPRO
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-12 mb-2">
                                <button 
                                    type="button"
                                    className={`btn btn-block modal-rips-btn-enviar ${botonEnviarHabilitado() ? 'btn-modal-rips-cta' : 'btn-secondary'}`}
                                    onClick={handleEnviarSispro}
                                    disabled={!botonEnviarHabilitado() || validandoFactura}
                                    title={getTooltipEnviar()}
                                >
                                    {enviando ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Enviando a SISPRO...
                                        </>
                                    ) : validandoFactura ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Validando...
                                        </>
                                    ) : estadoEnvio === 'enviado' ? (
                                        <>
                                            <i className="fas fa-check-circle"></i> Ya Enviado a SISPRO
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-paper-plane"></i> Enviar a SISPRO
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {factura_id && (
                            <div className="row">
                                <div className="col-md-12">
                                    <button 
                                        type="button"
                                        className="btn btn-outline-rips-slate btn-block"
                                        onClick={() => { window.location.href = `/#/informacion-factura/${factura_id}`; }}
                                    >
                                        <i className="fas fa-file-invoice mr-1"></i>
                                        Ver factura
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer bg-light border-top">
                        <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ModalRipsExito;
